// 2024-07-17: This file is a modified version of the original App.tsx, 
// renamed to App-dj.tsx for migration to dj auth. 
// It includes changes to the authentication flow, customer data handling
import { useState, useEffect, type JSX, createContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
// import { useAuth } from "./hooks/useAuth_SES";

import { CustomerData } from "./context/CustomerContext";
import AppCustomer from "./AppCustomer-dj";
import AppNewbie from "./AppNewbie-dj";
import { getCustomer } from "./api/data/customerTools";
import { Box, createTheme, ThemeProvider } from "@mui/material";
import { pointApi } from "./api/data/pointApi";
import axios from "axios";
import { getMenuItems } from "./menuItems_i18n";
import { useTranslation } from "react-i18next";
import { ColorModeContext } from './components/Shared/ThemeToggleButton';
import { DbThemeProvider } from "./components/themes/DbThemeContext";
// import { useLocalStorage } from "./hooks/useLocalStorage";
// import { basename } from "./globals_VITE";

function App(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();

  if (auth.tokens?.accessToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${auth.tokens.accessToken}`;
  }
  // const accessToken = useLocalStorage('access-token', null);
  // if (accessToken)
  //   axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  // const [customerData, _setCustomerData] = useState<CustomerData | null>(null);

  // const setCustomerData = (value: React.SetStateAction<CustomerData | null>) => {
  //   console.groupCollapsed('[CustomerData Change]');
  //   console.trace('Вызов setCustomerData из:');
    
  //   _setCustomerData((prev) => {
  //     const next = typeof value === 'function' ? value(prev) : value;
  //     console.log('Старое значение:', prev);
  //     console.log('Новое значение:', next);
  //     if (prev?.points && !next?.points) {
  //       console.error('🚨 ПОЙМАНА ОЧИСТКА МАССИВА POINTS! Смотри trace выше.');
  //     }
  //     console.groupEnd();
  //     return next;
  //   });
  // };

  const [isLoadingCustomerData, setIsLoadingCustomerData] = useState<boolean>(true);
  const [isLoadingPoints, setIsLoadingPoints] = useState(false);
  const appTitle = "People Counting";

  const isAuthLoading = typeof auth.userId === null;

  const [mode, setMode] = useState<'light' | 'dark'>('light');
  // Мемоизируем объект контекста, чтобы избежать лишних ререндеров
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );
  
  // Мемоизируем объект темы
  // Тема будет пересоздаваться ТОЛЬКО когда меняется 'mode'
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode, // <--- Здесь используется актуальное состояние 'mode'
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
          ...(mode === 'dark' && {
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
            text: {
              primary: '#ffffff',
              secondary: '#cccccc',
            },
          }),
        },
        typography: {
          fontFamily: 'Roboto, sans-serif',
          // Другие настройки типографики
        },
        // Другие настройки компонентов, spacing и т.д.
      }),
    [mode], // тема пересоздается при изменении 'mode'
  );

  useEffect(() => {
    document.title = appTitle;
  }, [appTitle]);

  const authRef = useRef(auth);
  authRef.current = auth;

  // if(1) vs if(0) <- разная настройка axios
if(1)
  useEffect(() => {
    axios.defaults.baseURL = "/api";

    // 1. REQUEST INTERCEPTOR: динамически подставляет токен перед КАЖДЫМ запросом
    const reqInterceptor = axios.interceptors.request.use((config) => {

      const token = authRef.current.tokens?.accessToken;

      console.log(`[REQ INTERCEPTOR] URL: ${config.url}`, {
          tokenFromRef: token,
          tokenFromStorage: localStorage.getItem("accessToken"), // замените на ваш ключ storage
        });

      // const token = authRef.current.tokens?.accessToken || localStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // 2. RESPONSE INTERCEPTOR: обрабатывает 401 и обновляет токен
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          // error.response?.data?.code === "token_not_valid" &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            await authRef.current.refresh();
            
            // Берём свежий токен после рефреша
            const newToken = authRef.current.tokens?.accessToken;
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return axios(originalRequest);
          } catch (refreshError) {
            authRef.current.logout();
            navigate("/login");
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

if(1)
  useEffect(() => {
    if (!isAuthLoading && auth.userId) {
      setIsLoadingCustomerData(false);

      if (auth.customerId) {
        // const newCustomerData = {
        //   customer: auth.customerId,
        //   point_id: 0,
        // };
        // setCustomerData(newCustomerData as CustomerData);
        // console.log("[App] Extracted customer data:", newCustomerData);
        setCustomerData((prevData) => {
          // Если клиент не менялся, сохраняем всё старое состояние (включая points)
          if (prevData && prevData.customer === auth.customerId) {
            return prevData;
          }
          // Если ID клиента действительно изменился — создаем новый объект
          return {
            customer: auth.customerId,
            point_id: 0,
            points: prevData?.points, // по желанию можно оставить точки, либо сбросить
          } as CustomerData;
        });
      } else {
        setCustomerData(null);
        console.log("[App] User object or user.data is not available for customer data extraction.");
        navigate("/settings/general");
      }
    } else {
      setIsLoadingCustomerData(true);
    }
  }, [auth.userId, auth.customerId, isAuthLoading, navigate]);

if(0)
  useEffect(() => {

    if (auth.tokens.accessToken && auth?.userId) {
      console.log("Tokens:", auth.tokens);
      console.log("User:", auth.userId);
      console.log("mode:", auth.mode);
      console.log("User roles:", auth.roles);

      axios.defaults.headers.common["Content-Type"] = "application/json";
      axios.defaults.headers.common["Authorization"] = `Bearer ${auth.tokens.accessToken}`;
   // axios.defaults.baseURL = basename + "/api/pcnt/";
   // axios.defaults.baseURL = "/api/pcnt/";
      axios.defaults.baseURL = "/api";
    } else {
      console.log("No tokens");
    }

    if (! isAuthLoading && auth.userId) {
      setIsLoadingCustomerData(false);

      if (auth.customerId) {
        const newCustomerData = {
          customer: auth.customerId,
          point_id: 0,
        };
        setCustomerData(newCustomerData as CustomerData);
        console.log("[App] Extracted customer data:", newCustomerData);
      } else {
        setCustomerData(null);
        console.log("[App] User object or user.data is not available for customer data extraction.");
        navigate("/settings/general");
      }
    } else {
      setIsLoadingCustomerData(true);
    }

    // Interceptor на 403 для автоматического рефреша токена и повторного запроса
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // if (error.response?.status === 403 && !originalRequest._retry) {
        if (
          error.response?.status === 401 && 
          error.response?.data?.code === "token_not_valid" && 
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            await auth.refresh();
            // После рефреша useAuth обновит auth.tokens.accessToken,
            // но здесь нам нужен свежий токен синхронно — берём из auth или из хранилища
            const newToken = auth.tokens.accessToken;
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            auth.logout();
            navigate("/login");
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Очистка: убираем interceptor при смене токена или анмаунте
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    
  }, [auth.tokens.accessToken, auth.userId, isAuthLoading]);


  const { t } = useTranslation();

  const menuItems = useMemo(
    () => getMenuItems(auth, t),
    [auth.userId, auth.role, t]
  );

  useEffect(() => {
    const loadPoints = async () => {
      console.log("[App] isLoadingPoints:", isLoadingPoints);
      if (customerData?.customer) {
        setIsLoadingPoints(true);
        try {
          console.log(`[App] Fetching points for customer ID: ${customerData.customer}`);

          const points = (await pointApi.getPoints()).map((point) => ({
            value: (point as any).point_id,
            label: (point as any).name,
          }));

          setCustomerData((prevData) =>
            prevData ? { ...prevData, points } : null
          );
          console.log(`[App] Fetched points for customer ${customerData.customer}:`,points);
        } catch (error) {
          console.error(
            `[App] Error fetching points for customer ${customerData.customer}:`,
            error
          );
          setCustomerData((prevData) =>
            prevData ? { ...prevData, points: [] } : null
          );
        } finally {
          setIsLoadingPoints(false);
        }
      } else if (customerData && customerData.points !== undefined) {
        setCustomerData((prevData) => prevData ? { ...prevData, points: undefined } : null);
        setIsLoadingPoints(false);
      }
    };

    const loadCustomerName = async () => {
      const dbCustomer = await getCustomer(customerData?.customer || 0);
      if (dbCustomer && dbCustomer.length > 0) {
        document.title = ` ${appTitle} - ${dbCustomer[0].legal_name}`;
      }
    };

    console.log("[App] customerData:", customerData);
    if(customerData) {
      if (customerData.customer) {
        loadPoints();
        loadCustomerName();
      } else {
        navigate("/settings/general");
      }
    } else {
      if (!auth.customerId || 0) {
        navigate("/settings/general");
      }
    }

  }, [customerData?.customer]); 

  const handleLogout = () => {
    auth.logout();
    navigate("/login");
  };

  if (typeof auth.userId === "undefined") {
  // if (typeof auth.userId === "undefined" || isLoadingCustomerData || isLoadingPoints) {
    return <div>Завантаження ...</div>;
  }

  if (!auth.tokens.accessToken || !auth.userId) {
    return (
      // <DbThemeProvider>
        <AppNewbie appTitle={appTitle} />
      // </DbThemeProvider>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <ColorModeContext.Provider value={colorMode}>
      <DbThemeProvider>
        <AppCustomer
          user={auth.userId}
          menuItems={menuItems}
          appTitle={appTitle}
          onLogout={handleLogout}
          customerData={customerData}
          isLoadingCustomerData={isLoadingCustomerData}
        />
      </DbThemeProvider>
      </ColorModeContext.Provider>
    </Box>
  );
}

export default App;